import React, { useEffect } from 'react';
import { safePostMessageToParent } from '@charitystack/shared';
import { DYNAMIC_FORM_MIN_HEIGHT_PX, DYNAMIC_FORM_MIN_HEIGHT } from './formHeightUtils';
import FormSkeletonCard from '../Components/skeleton/FormSkeletonCard';
import DetailPageSkeleton from './DetailPageSkeleton';

const STATUS_LABEL = 'Loading donation form';

/**
 * The donation form's loading frame (owner call 2026-10-23): one per form
 * type, each the shape of that type's first step, so nothing moves when the
 * form replaces it. The frames are composed in Components/skeleton/.
 *
 * `formType` picks the frame (EMBED_FORM / STANDARD_FORM / legacy
 * DONATION_FORM → standard, CROWDFUNDING_FORM → giving levels, PRODUCT →
 * sponsorships, EVENT → tickets). `config`, when the form's configuration is
 * already known, sizes it to this form; `page` is the step the form will open
 * on ('DETAIL_PAGE' draws the detail card).
 */
const FormSkeleton = ({
  formType = 'STANDARD_FORM',
  elementID = '',
  domID = '',
  isDrawerOpen = false,
  formDisplayMode,
  config = null,
  page = 'FORM',
  isHostedPage = false,
  /**
   * Skip this component's own `.page-container` wrapper, because the caller
   * already has one (CON-160).
   *
   * `EmbedForm` renders the placeholder and the form into the SAME container
   * div so the swap is not a remount. Without this prop that would nest two
   * `.page-container`s, and the outer one would hold a stale height. Every
   * other consumer passes nothing and is unchanged.
   */
  bare = false,
}) => {
  // Use the same minimum height as the real form (from formHeightUtils).
  // CSS container queries may override to a smaller value at narrow widths,
  // but the skeleton resize message uses this as the floor.
  const minHeight = DYNAMIC_FORM_MIN_HEIGHT_PX;

  // Trigger iframe resize when skeleton mounts
  useEffect(() => {
    const resizeTimeout = setTimeout(() => {
      const currentHeight = Math.max(document.body.scrollHeight, minHeight);
      const currentWidth = document.body.scrollWidth;

      safePostMessageToParent({
        action: 'RESIZE',
        entity: 'CHARITYSTACK',
        sender: 'EMBED_FORM',
        receiver: 'HOSTED_SCRIPT',
        data: JSON.stringify({
          height: currentHeight,
          width: currentWidth,
          elementID: elementID,
          domID: domID,
          transitionDuration: 50,
          contentAnimationDuration: 1000,
          easing: 'ease-out',
          delay: 0,
        }),
      }, '*');
    }, 50);

    return () => clearTimeout(resizeTimeout);
  }, [minHeight, elementID, domID]);

  const status = { role: 'status', 'aria-live': 'polite', 'aria-label': STATUS_LABEL };

  // The frame for this form type (and, once a response is in, this form) —
  // see FormSkeletonCard. A detail-page first step gets the detail card.
  const frame = (statusProps) =>
    page === 'DETAIL_PAGE' && !isDrawerOpen ? (
      // Event detail pages are taller (.event-detail-height), so the event card keeps its height.
      <DetailPageSkeleton formDisplayMode={formDisplayMode} isEvent={formType === 'EVENT'} statusProps={statusProps} />
    ) : (
      <FormSkeletonCard
        formType={formType}
        config={config}
        mode={isDrawerOpen ? 'drawer' : formDisplayMode === 'overlay' ? 'overlay' : isHostedPage ? 'hosted' : 'dynamic'}
        statusProps={statusProps}
      />
    );

  // ─── Drawer mode: flat, stretches to fill mobile-drawer-content min-height ──
  if (isDrawerOpen) {
    return (
      <div className="flex h-full w-full flex-col" style={{ minHeight: DYNAMIC_FORM_MIN_HEIGHT }}>
        {frame(status)}
      </div>
    );
  }

  // When the caller owns the container, the loading state has to be
  // announced on the card instead — `role="status"` lives on the wrapper below.
  const card = frame(bare ? status : {});

  if (bare) return card;

  return (
    <div
      className="page-container"
      style={{
        minHeight: 'fit-content',
        height: 'auto',
      }}
      role="status"
      aria-live="polite"
      aria-label={STATUS_LABEL}
    >
      <span className="sr-only">{STATUS_LABEL}, please wait...</span>
      {card}
    </div>
  );
};

export default FormSkeleton;
